import { gql, useMutation } from '@apollo/client';
import React from 'react';
import type {
  CreateTopicMutation,
  CreateTopicMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import {
  TopicMemberEditable,
  TopicPrivacy,
} from '../../graphql/generated/graphqlTypes';
import UIActions from '../../ui/UIActions';
import topicPath from '../../utils/url/topicPath';
import { useRouter } from '../../utils/router/v3Bridge';
import TopicModal from './TopicModal';
import { useAppDispatch } from '../../app/redux/useAppSelector';

const CREATE_TOPIC_MUTATION = gql`
  mutation createTopic(
    $name: String!
    $description: Json
    $memberEditable: TopicMemberEditable
    $parentId: ID
    $privacy: TopicPrivacy
    $inheritParent: Boolean
  ) {
    createTopic(
      name: $name
      description: $description
      memberEditable: $memberEditable
      parentId: $parentId
      privacy: $privacy
      inheritParent: $inheritParent
    ) {
      id
    }
  }
`;

const DEFAULT_MEMBER_EDITABLE = TopicMemberEditable.All;
const DEFAULT_PRIVACY = TopicPrivacy.Open;

interface NewTopicModalProps {
  close: () => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCreate: (newTopic: any) => void;
  name?: string;
  parentId?: string;
}

const NewTopicModal = ({
  close,
  name,
  parentId,
  onCreate,
}: NewTopicModalProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [createTopic] = useMutation<
    CreateTopicMutation,
    CreateTopicMutationVariables
  >(CREATE_TOPIC_MUTATION);

  const onSubmit = async (values: CreateTopicMutationVariables) => {
    const response = await createTopic({
      variables: {
        ...values,
        inheritParent:
          values.privacy !== TopicPrivacy.Private &&
          values.privacy !== TopicPrivacy.Secret,
        description: JSON.stringify(values.description),
      },
    });
    const id = response.data?.createTopic?.id;

    if (id) {
      if (onCreate) {
        onCreate({ ...values, id });
      } else {
        router.push({
          pathname: topicPath({ id, name: values.name }),
          state: { edit: true, new: true },
        });
      }
      dispatch(UIActions.closeModal(true));
    }
  };

  return (
    <TopicModal
      close={close}
      name={name || ''}
      privacy={DEFAULT_PRIVACY}
      memberEditable={DEFAULT_MEMBER_EDITABLE}
      onSubmit={onSubmit}
      parentId={parentId}
      submitText="Create topic"
      title="Create a topic"
    />
  );
};

export default NewTopicModal;
